<template lang="pug">
.image-hover(
  ref="imageHoverElRef"
  :alt="alt || undefined"
  :title="alt || undefined"
  :role="alt ? 'img' : 'none'"
  :class="twMerge('w-[16rem]', classAttr)"
  :style="{ 'width': width }"
)
  .images.relative.overflow-hidden
    uc-asset.base.w-full(
      :class="baseStyle"
      :src="base"
      role="none"
    )
    uc-asset.over.w-full.absolute.top-0.left-0(
      :class="overStyle"
      :src="over"
      role="none"
      uc-word-skip=""
    )
</template>

<script setup lang="ts">
import { computed, onMounted, ref, unref, useAttrs } from 'vue';
import { twMerge } from '../lib/mergeStyles';
import gsap from 'gsap';

const props = defineProps<{
  alt?: string;
  effect?: string;
  base: string;
  over: string;
  size?: number;
  baseStyle?: string;
  overStyle?: string;
}>();

const EFFECT_FADE = 'fade';
const EFFECT_FADE_OVER = 'fade-over';
const EFFECT_SLIDE_LEFT = 'slide-left';
const EFFECT_SLIDE_RIGHT = 'slide-right';
const EFFECT_SLIDE_UP = 'slide-up';
const EFFECT_SLIDE_DOWN = 'slide-down';
const EFFECT_WIPE_LEFT = 'wipe-left';
const EFFECT_WIPE_RIGHT = 'wipe-right';
const EFFECT_WIPE_UP = 'wipe-up';
const EFFECT_WIPE_DOWN = 'wipe-down';

const imageHoverElRef = ref<HTMLElement | null>(null);
const classAttr = computed(() => useAttrs().class as string | undefined);
const width = computed(() => (props.size ? props.size + 'rem' : ''));

onMounted(() => {
  const imageHoverEl = unref(imageHoverElRef);

  if (!imageHoverEl) return;

  imageHoverEl.addEventListener('mouseenter', e => hover('over'));
  imageHoverEl.addEventListener('mouseleave', e => hover('out'));

  const imgHovBase = imageHoverEl.querySelector(
    '.uc-asset.base'
  ) as HTMLElement;
  const imgHovOver = imageHoverEl.querySelector(
    '.uc-asset.over'
  ) as HTMLElement;

  const topPos =
    props.effect === EFFECT_SLIDE_UP
      ? '+100%'
      : props.effect === EFFECT_SLIDE_DOWN
        ? '-100%'
        : 0;
  const leftPos =
    props.effect === EFFECT_SLIDE_LEFT
      ? '+100%'
      : props.effect === EFFECT_SLIDE_RIGHT
        ? '-100%'
        : 0;

  let wipePadding;

  if (props.effect === EFFECT_WIPE_LEFT) wipePadding = '0 0 0 100%';
  else if (props.effect === EFFECT_WIPE_RIGHT) wipePadding = '0 100% 0 0';
  else if (props.effect === EFFECT_WIPE_UP) wipePadding = '100% 0 0 0';
  else if (props.effect === EFFECT_WIPE_DOWN) wipePadding = '0 0 100% 0';

  let wipeObjectPosition =
    props.effect === EFFECT_WIPE_LEFT
      ? 'right'
      : props.effect === EFFECT_WIPE_RIGHT
        ? 'left'
        : props.effect === EFFECT_WIPE_UP
          ? 'bottom'
          : 'top';

  // hide overlay image initially
  imgHovOver.style.opacity = '0';

  function fadeIn(el) {
    gsap.to(el, {
      opacity: 1,
      duration: 0.5,
      ease: 'linear'
    });
  }

  function fadeOut(el) {
    gsap.to(el, {
      opacity: 0,
      duration: 0.5,
      ease: 'linear'
    });
  }

  function slideIn(el) {
    el.style.opacity = 0;
    el.style.left = leftPos;
    el.style.top = topPos;

    gsap.to(el, {
      opacity: 1,
      top: 0,
      left: 0,
      duration: 0.5,
      ease: 'easeOut'
    });
  }

  function slideOut(el) {
    el.style.opacity = 1;
    el.style.left = 0;
    el.style.top = 0;

    gsap.to(el, {
      opacity: 0,
      top: topPos,
      left: leftPos,
      duration: 0.5,
      ease: 'easeOut'
    });
  }

  function wipeIn(el) {
    el.style.opacity = 1;
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.style.objectPosition = wipeObjectPosition;
    el.style.padding = wipePadding;

    gsap.to(el, {
      padding: 0,
      duration: 0.5,
      ease: 'easeOut'
    });
  }

  function wipeOut(el) {
    el.style.opacity = 1;
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.style.objectPosition = wipeObjectPosition;
    el.style.padding = 0;

    gsap.to(el, {
      padding: wipePadding,
      duration: 0.5,
      ease: 'easeOut'
    });
  }

  const hover = state => {
    switch (props.effect) {
      case EFFECT_SLIDE_LEFT:
      case EFFECT_SLIDE_RIGHT:
      case EFFECT_SLIDE_UP:
      case EFFECT_SLIDE_DOWN:
        if (state === 'over') {
          slideIn(imgHovOver);
          fadeOut(imgHovBase);
        } else {
          slideOut(imgHovOver);
          fadeIn(imgHovBase);
        }

        break;

      case EFFECT_WIPE_LEFT:
      case EFFECT_WIPE_RIGHT:
      case EFFECT_WIPE_UP:
      case EFFECT_WIPE_DOWN:
        if (state === 'over') {
          wipeIn(imgHovOver);
        } else {
          wipeOut(imgHovOver);
        }

        break;

      case EFFECT_FADE_OVER:
        if (state === 'over') {
          fadeIn(imgHovOver);
        } else {
          fadeOut(imgHovOver);
        }

        break;

      case EFFECT_FADE:
      default:
        if (state === 'over') {
          fadeIn(imgHovOver);
          fadeOut(imgHovBase);
        } else {
          fadeIn(imgHovBase);
          fadeOut(imgHovOver);
        }

        break;
    }
  };
});
</script>

<style scoped></style>
